import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { UserCheck, UserX, MessageSquare, Swords } from "lucide-react";
import LevelBadge from "@/components/level/LevelBadge";
import type { Tables } from "@/integrations/supabase/types";

interface FriendProfile {
  id: string;
  full_name: string | null;
  level: number | null;
  player_type: string | null;
  avatar_url: string | null;
}

interface ConnectionWithProfile extends Tables<"player_connections"> {
  partnerId: string;
  profile: FriendProfile;
  isIncoming: boolean;
}

export default function FriendsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: connections = [], isLoading } = useQuery({
    queryKey: ["friends", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("player_connections")
        .select("*")
        .or(`user_id.eq.${user!.id},friend_id.eq.${user!.id}`)
        .order("created_at", { ascending: false });
      if (error) throw error;

      const rawConnections = (data ?? []) as Tables<"player_connections">[];
      const partnerIds = rawConnections.map((c) =>
        c.user_id === user!.id ? c.friend_id : c.user_id
      );
      if (partnerIds.length === 0) return [];

      const { data: profiles } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name, level, player_type, avatar_url")
        .in("id", partnerIds);

      const profileMap = new Map((profiles ?? []).map((p) => [p.id, p as FriendProfile]));

      return rawConnections.map((c): ConnectionWithProfile => {
        const partnerId = c.user_id === user!.id ? c.friend_id : c.user_id;
        return {
          ...c,
          partnerId,
          profile: profileMap.get(partnerId) ?? { id: partnerId, full_name: "Okänd", level: 1, player_type: null, avatar_url: null },
          isIncoming: c.friend_id === user!.id,
        };
      });
    },
    enabled: !!user?.id,
  });

  const acceptMutation = useMutation({
    mutationFn: async (connectionId: string) => {
      const { error } = await supabase
        .from("player_connections")
        .update({ status: "accepted" })
        .eq("id", connectionId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["friends"] });
      toast.success("Vänförfrågan accepterad!");
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (connectionId: string) => {
      const { error } = await supabase
        .from("player_connections")
        .delete()
        .eq("id", connectionId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["friends"] });
      toast.success("Borttagen");
    },
  });

  const accepted = connections.filter((c) => c.status === "accepted");
  const pending = connections.filter((c) => c.status === "pending" && c.isIncoming);
  const sent = connections.filter((c) => c.status === "pending" && !c.isIncoming);

  if (isLoading) return <div className="space-y-2">{[1,2].map(i => <Skeleton key={i} className="h-16" />)}</div>;

  const pendingCount = pending.length;

  return (
    <Tabs defaultValue="friends">
      <TabsList className="w-full">
        <TabsTrigger value="friends" className="flex-1">
          Vänner {accepted.length > 0 && `(${accepted.length})`}
        </TabsTrigger>
        <TabsTrigger value="requests" className="flex-1 relative">
          Förfrågningar
          {pendingCount > 0 && (
            <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground px-1">
              {pendingCount}
            </span>
          )}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="friends" className="mt-3 space-y-2">
        {accepted.length === 0 ? (
          <p className="text-center text-muted-foreground py-8 text-sm">
            Inga vänner ännu. Sök efter spelare och lägg till!
          </p>
        ) : (
          accepted.map((c) => (
            <Card key={c.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/players/${c.partnerId}`)}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold">{(c.profile.full_name?.[0] || "?").toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{c.profile.full_name}</p>
                </div>
                <LevelBadge level={c.profile.level ?? 1} size="sm" />
                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/messages/${c.partnerId}`); }}>
                  <MessageSquare className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/book`); }}>
                  <Swords className="h-3.5 w-3.5" />
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </TabsContent>

      <TabsContent value="requests" className="mt-3 space-y-3">
        {/* Incoming */}
        {pending.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground">Mottagna</h3>
            {pending.map((c) => (
              <Card key={c.id}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center shrink-0">
                    <span className="text-xs font-bold">{(c.profile.full_name?.[0] || "?").toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{c.profile.full_name}</p>
                  </div>
                  <LevelBadge level={c.profile.level ?? 1} size="sm" />
                  <Button size="sm" variant="default" onClick={() => acceptMutation.mutate(c.id)} className="gap-1">
                    <UserCheck className="h-3.5 w-3.5" /> Acceptera
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => removeMutation.mutate(c.id)}>
                    <UserX className="h-3.5 w-3.5" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Sent */}
        {sent.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground">Skickade</h3>
            {sent.map((c) => (
              <Card key={c.id}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center shrink-0">
                    <span className="text-xs font-bold">{(c.profile.full_name?.[0] || "?").toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{c.profile.full_name}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">Väntande</Badge>
                  <Button size="sm" variant="ghost" onClick={() => removeMutation.mutate(c.id)}>
                    <UserX className="h-3.5 w-3.5" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {pending.length === 0 && sent.length === 0 && (
          <p className="text-center text-muted-foreground py-8 text-sm">Inga förfrågningar</p>
        )}
      </TabsContent>
    </Tabs>
  );
}
